import React, { useMemo } from 'react';

// Phase 2.C — Chart-pane procedure picker. Reuses `QuickSearchAdd` (the
// same component the right-edge rail mounts) but frames it for inline
// rendering INSIDE the chart pane's portal target. Reaches into the
// legacy procedures-widget folder for the picker — acknowledged
// boundary stretch tracked in `src/charts/README.md`. When
// `QuickSearchAdd` graduates to `src/widgets/forms/QuickProcedureAdd`
// in a future PR, swap the import to the public SDK and delete this
// note.
import QuickSearchAdd from '../../../components/procedures/widget/QuickSearchAdd';

/**
 * ChartPaneProcedurePicker — inline procedure picker rendered inside
 * the chart pane when the dentist clicks "+ Add procedure" or
 * "→ Plan procedure for this" from the chart. Eliminates the eye
 * travel from chart center to right-edge rail; the picker mounts
 * right next to the tooth chart UI.
 *
 * Props:
 *   visit   — visit row
 *   viewer  — { memberId, role }
 *   prefill — { tokens, diagnosisMetaId, summary, requestId }
 *   onClose — close callback (chart calls sdk.closeWidget())
 *   onCreated — () => void; chart refreshes its sittings list
 *   isDark  — theme flag
 */
export default function ChartPaneProcedurePicker({
  visit, viewer, prefill = null, onClose, onCreated, isDark = false,
}) {
  const palette = useMemo(() => ({
    bg:     isDark ? '#0f172a' : '#ffffff',
    bgAlt:  isDark ? '#1e293b' : '#f8fafc',
    border: isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.12)',
    text:   isDark ? '#e2e8f0' : '#1e293b',
    muted:  isDark ? '#94a3b8' : '#64748b',
    accent: '#0d9488',
  }), [isDark]);

  return (
    <div
      className={[
        'rounded-md border p-3 flex flex-col gap-3',
        isDark
          ? 'bg-slate-900 border-slate-700 text-slate-100'
          : 'bg-white border-slate-300 text-slate-900',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          <button
            type="button"
            onClick={onClose}
            className="px-1.5 py-0.5 text-[11px] rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
            aria-label="Back to chart"
            title="Back to chart"
          >
            ←
          </button>
          <span>Add procedure</span>
        </h3>
      </div>

      <QuickSearchAdd
        visit={visit}
        viewer={viewer}
        palette={palette}
        autoFocus
        prefilledDiagnosisMetaId={prefill?.diagnosisMetaId || null}
        prefilledDiagnosisSummary={prefill?.summary || null}
        prefilledTokens={prefill?.tokens || null}
        prefilledProcedureId={prefill?.procedureId || null}
        prefillRequestId={prefill?.requestId || null}
        onCreated={() => {
          try { onCreated?.(); } catch (_e) { /* ignore */ }
          onClose?.();
        }}
      />
    </div>
  );
}
